import { Fragment, useEffect, useState } from 'react';
import { tradeApi } from './api.js';
import { StatusTag, accountSaid } from './RequestDetail.jsx';
import { FOLLOW_UPS, money, typeLabels, taxLabel } from './fields.js';

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function day(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const CLOSED = ['Completed', 'Cancelled'];

function SfLink({ base, id, children }) {
  if (!base || !id) return <>{children}</>;
  return <a href={`${base}/${id}`} target="_blank" rel="noopener noreferrer">{children}</a>;
}

function CaseLine({ c, sfBase }) {
  return (
    <li>
      <SfLink base={sfBase} id={c.id}><span className="tt-mono">{c.number}</span></SfLink> {c.re || c.subject}
      <span className="tt-muted"> · {[c.recordType, c.type, c.subtype].filter(Boolean).join(' · ')}</span>
      {c.systematic ? <span className="tt-muted"> · systematic, {c.systematic.toLowerCase()}{c.amount ? ` ${money(c.amount)}` : ''}</span> : c.amount ? <span className="tt-muted"> · {money(c.amount)}</span> : null}
      <span className="tt-tag">{c.status}</span>
      {c.dueDate && <span className="tt-muted"> · due {day(c.dueDate)}</span>}{c.owner ? <span className="tt-muted"> · {c.owner}</span> : null}
    </li>
  );
}

/** The follow-up cases a trader opens from the request, each the case the team opens by hand today. */
function FollowUps({ accounts, disabled, onOpen }) {
  const [picked, setPicked] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const set = (kind, patch) => setPicked(p => ({ ...p, [kind]: { ...(p[kind] || {}), ...patch } }));
  const chosen = FOLLOW_UPS.filter(f => picked[f.kind] && picked[f.kind].on);
  async function open() {
    setSending(true);
    setError(null);
    const followUps = chosen.map(f => ({ kind: f.kind, ...(picked[f.kind] || {}), on: undefined }));
    const res = await onOpen(followUps);
    setSending(false);
    if (!res.ok) { setError(res.body.error); return; }
    setPicked({});
  }
  return (
    <div className="tt-followups">
      {error && <div className="tt-banner is-error" role="alert">{error}</div>}
      {FOLLOW_UPS.map(f => {
        const p = picked[f.kind] || {};
        return (
          <div key={f.kind} className={'tt-followup' + (p.on ? ' is-on' : '')}>
            <label className="tt-field is-check">
              <input type="checkbox" checked={Boolean(p.on)} disabled={disabled} onChange={e => set(f.kind, { on: e.target.checked })} />
              <span>{f.label} <span className="tt-help tt-inline-help">{f.help}</span></span>
            </label>
            {p.on && (
              <div className="tt-grid tt-followup-fields">
                {accounts.length > 1 && (
                  <div className="tt-field">
                    <label>Account</label>
                    <select value={p.accountId || ''} onChange={e => set(f.kind, { accountId: e.target.value })}>
                      <option value="">First account on the request</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                )}
                {f.needsTicker && (
                  <div className="tt-field">
                    <label>Ticker</label>
                    <input type="text" value={p.ticker || ''} onChange={e => set(f.kind, { ticker: e.target.value.toUpperCase() })} />
                  </div>
                )}
                {f.needsDate && (
                  <div className="tt-field">
                    <label>{f.kind === 'intervalFund' ? 'Tender date' : 'When'}</label>
                    <input type="date" value={p.date || ''} onChange={e => set(f.kind, { date: e.target.value })} />
                  </div>
                )}
                <div className="tt-field is-wide">
                  <label>{f.needsDetail ? 'What to do' : 'Note (optional)'}</label>
                  <input type="text" value={p.detail || ''} onChange={e => set(f.kind, { detail: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className="tt-actions">
        <button className="btn btn-light" disabled={disabled || sending || !chosen.length} onClick={open}>{sending ? 'Opening…' : chosen.length > 1 ? `Open ${chosen.length} cases` : 'Open the case'}</button>
      </div>
    </div>
  );
}

/**
 * One request as a trader works it: the advisor's answers, every account with what Salesforce holds around it
 * (cash, reserves, systematics, exclusions, PureFlow, the manager), the household, its open cases, what was
 * done for these accounts before, the conversation, and the actions.
 */
export default function ImRequest({ id, navigate, onLoaded }) {
  const [state, setState] = useState({ state: 'loading' });
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [discretion, setDiscretion] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }

  async function load() {
    const res = await tradeApi.imRequest(id);
    if (!res.ok) { setState({ state: 'failed', error: res.body.error }); return; }
    setState({ state: 'ready', ...res.body });
    setNote(res.body.request.tradersNote || '');
    if (onLoaded) onLoaded(res.body.request);
  }
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(body, done) {
    setBusy(true);
    setNotice(null);
    const res = await tradeApi.imAct(id, body);
    setBusy(false);
    if (!res.ok) { setNotice({ tone: 'is-error', text: res.body.error || 'Salesforce refused the change.' }); return res; }
    setNotice({ tone: 'is-ok', text: done });
    await load();
    return res;
  }

  if (state.state === 'loading') {
    return <div className="tt-loading"><div className="load-spinner" /><p>Reading the request from Salesforce…</p></div>;
  }
  if (state.state === 'failed') {
    return <div className="tt-page"><div className="tt-banner is-error" role="alert"><strong>The request could not be opened.</strong> {state.error}</div></div>;
  }

  const { request, household, openCases, followUpCases, previousRequests, messages, userId, salesforceUrl } = state;
  const closed = CLOSED.includes(request.status);
  const mine = request.assignedTraderId === userId;
  const answers = Object.values(request.answers || {});
  const noteChanged = (note || '') !== (request.tradersNote || '');

  return (
    <div className="tt-page tt-wide">
      <header className="tt-page-head">
        <div>
          <p className="tt-kicker">Investment Management · {typeLabels(request.types)}</p>
          <h1>{request.name} <StatusTag status={request.status} /></h1>
          <p className="tt-muted">
            <SfLink base={salesforceUrl} id={request.householdId}>{request.household}</SfLink> · sent {when(request.createdDate)} by {request.createdBy}
            {request.fromWizard ? ' through the Move Money wizard' : request.source ? ` through ${request.source}` : ''}
            {request.assignedTrader && <> · with <strong>{request.assignedTrader}</strong></>}
            {request.replaces && <> · replaces {request.replacesName}</>}
            {' · '}<SfLink base={salesforceUrl} id={request.id}>open in Salesforce</SfLink>
          </p>
        </div>
        <button className="btn btn-light" onClick={() => navigate('/trade/im')}>Queue</button>
      </header>

      {notice && <div className={'tt-banner ' + notice.tone} role="status">{notice.text}</div>}

      <section className="tt-card tt-actionbar">
        {!closed && !mine && <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => act({ action: 'claim' }, 'The request is yours.')}>Claim</button>}
        {!closed && mine && <button className="btn btn-light btn-sm" disabled={busy} onClick={() => act({ action: 'release' }, 'Released back to the queue.')}>Release</button>}
        {!closed && request.status !== 'Request on Hold' && <button className="btn btn-light btn-sm" disabled={busy} onClick={() => act({ action: 'hold' }, 'On hold.')}>Put on hold</button>}
        {!closed && request.status === 'Request on Hold' && <button className="btn btn-light btn-sm" disabled={busy} onClick={() => act({ action: 'resume' }, 'Back in the queue.')}>Take off hold</button>}
        {!closed && (
          <span className="tt-complete">
            <label className="tt-radio"><input type="radio" name="tt-discretion" checked={discretion === 'NO'} onChange={() => setDiscretion('NO')} />No discretion exercised</label>
            <label className="tt-radio"><input type="radio" name="tt-discretion" checked={discretion === 'YES'} onChange={() => setDiscretion('YES')} />Discretion exercised</label>
            <button className="btn btn-primary btn-sm" disabled={busy || !discretion} onClick={() => act({ action: 'complete', discretion }, 'Completed.')}>Complete</button>
          </span>
        )}
        {closed && <span className="tt-muted">This request is {request.status.toLowerCase()}{request.discretion ? `, discretion ${request.discretion.toLowerCase()}` : ''}.</span>}
      </section>

      <div className="tt-two-col">
        <div>
          <section className="tt-card">
            <h2>From the advisor</h2>
            {request.instruction && <p className="tt-pre tt-instruction">{request.instruction}</p>}
            {request.specialInstructions && <p className="tt-pre tt-note"><strong>Special instructions:</strong> {request.specialInstructions}</p>}
            {answers.length > 0 && (
              <dl className="tt-summary tt-tight">
                {answers.map(a => <Fragment key={a.label}><dt>{a.label}</dt><dd>{a.value}</dd></Fragment>)}
              </dl>
            )}
            {request.notes && <p className="tt-note"><strong>Request notes:</strong> {request.notes}</p>}
            {request.linkedCase && <p className="tt-note">Linked case <SfLink base={salesforceUrl} id={request.linkedCase.id}>{request.linkedCase.number}</SfLink> {request.linkedCase.subject}</p>}
            {request.proxyCase && <p className="tt-note">Move Money case <SfLink base={salesforceUrl} id={request.proxyCase.id}>{request.proxyCase.number}</SfLink> {request.proxyCase.subject}</p>}
            {request.summary && <details className="tt-more"><summary>The advisor's answers as the ticket wrote them</summary><p className="tt-summary-block">{request.summary}</p></details>}
          </section>

          <section className="tt-card">
            <h2>Accounts on this request</h2>
            {request.accounts.map(a => {
              const said = accountSaid(a);
              return (
                <div key={a.id} className="tt-account tt-im-account">
                  <div className="tt-account-head">
                    <span className="tt-acct"><SfLink base={salesforceUrl} id={a.id}>{a.name}</SfLink></span>
                    {a.managedBy && a.managedBy !== 'Pure' && <span className="tt-badge">{a.managedBy}</span>}
                    {a.sma && <span className="tt-badge">SMA {a.sma}</span>}
                    {a.discretionary === false && <span className="tt-badge">non-discretionary</span>}
                    <span className="tt-muted"> · {a.number} · {a.type} · {taxLabel(a) ? taxLabel(a).toLowerCase() : a.taxType}{a.custodian ? ` · ${a.custodian}` : ''}</span>
                  </div>
                  <dl className="tt-facts tt-im-facts">
                    <div><dt>Value</dt><dd>{a.funded === false ? 'not funded yet' : money(a.value)}</dd></div>
                    <div><dt>Cash</dt><dd>{money(a.cash) || '$0'}{a.percentCash ? ` · ${Number(a.percentCash).toFixed(1)}%` : ''}{a.sweepCash ? <div className="tt-muted tt-small">sweep {money(a.sweepCash)}</div> : null}</dd></div>
                    <div><dt>Tamarac</dt><dd>{a.tamaracModel || 'no model yet'}{a.group ? <div className="tt-muted tt-small">group {a.group}</div> : null}</dd></div>
                    <div><dt>Guideline IPS</dt><dd>{a.ipsModel || '–'} · risk {a.riskProfile || '–'}{a.percentFixed ? <div className="tt-muted tt-small">{a.percentFixed}% fixed</div> : null}</dd></div>
                    <div><dt>PureFlow</dt><dd>{a.pureflowIncluded ? 'included' : 'not included'}{a.temporaryExclusion ? ' · temporary exclusion' : ''}</dd></div>
                    <div><dt>Gains budget on file</dt><dd>{a.gainsBudgetType ? `${a.gainsBudgetType === 'Dollar' ? money(a.gainsBudget) : a.gainsBudgetType === 'Percent' ? `${a.gainsBudget}%` : 'no limit'}${a.gainsBudgetUsed != null ? ` · ${a.gainsBudgetUsed}% used` : ''}` : '–'}</dd></div>
                  </dl>
                  {(a.cashReserves || []).length > 0 && (
                    <ul className="tt-onfile">
                      {a.cashReserves.map((r, i) => <li key={i}>Cash reserve: <strong>{money(r.amount)}</strong>{r.category ? ` · ${r.category}` : ''}{r.recurring ? ' · recurring' : ''}{r.startDate ? ` · from ${day(r.startDate)}` : ''}{r.endDate ? ` · until ${day(r.endDate)}` : ''}{r.note ? ` · ${r.note}` : ''}</li>)}
                    </ul>
                  )}
                  {(a.openCases || []).length > 0 && <ul className="tt-onfile">{a.openCases.map(c => <CaseLine key={c.id} c={c} sfBase={salesforceUrl} />)}</ul>}
                  {(a.activeAverages || []).length > 0 && (
                    <ul className="tt-onfile">{a.activeAverages.map(d => <li key={d.name}>Active dollar-cost average {d.name} · {money(d.amount)}{d.frequency ? ` ${d.frequency.toLowerCase()}` : ''}{d.nextTradeDate ? ` · next ${day(d.nextTradeDate)}` : ''}</li>)}</ul>
                  )}
                  {(a.excludedPositions || []).length > 0 && (
                    <ul className="tt-onfile">{a.excludedPositions.map(p => <li key={p.ticker}>Excluded: {p.ticker}{p.name ? ` ${p.name}` : ''}{p.reason ? ` · ${p.reason}` : ''}{p.comments ? ` · ${p.comments}` : ''}</li>)}</ul>
                  )}
                  {said.length > 0 && (
                    <div className="tt-said"><strong>The advisor said:</strong> {said.join(' · ')}</div>
                  )}
                </div>
              );
            })}
          </section>

          <section className="tt-card">
            <h2>Conversation with the advisor</h2>
            {messages.length === 0 && <p className="tt-muted">Nothing yet.</p>}
            <ol className="tt-thread">
              {messages.map(m => (
                <li key={m.id} className={m.direction === 'To advisor' ? 'is-me' : 'is-them'}>
                  <div className="tt-thread-meta">{m.author} · {when(m.createdDate)}</div>
                  <div className="tt-thread-body tt-pre">{m.body}</div>
                  {m.changes && <div className="tt-thread-changes tt-pre">{m.changes}</div>}
                </li>
              ))}
            </ol>
            {!closed && (
              <div className="tt-reply">
                <div className="tt-field is-wide">
                  <label htmlFor="tt-im-message">Send back to the advisor, or cancel with a reason</label>
                  <textarea id="tt-im-message" rows={3} value={message} onChange={e => setMessage(e.target.value)} />
                </div>
                <div className="tt-actions">
                  <button className="btn btn-primary" disabled={busy || !message.trim()} onClick={async () => { const r = await act({ action: 'sendBack', message }, 'Sent back to the advisor.'); if (r.ok) setMessage(''); }}>Send back to the advisor</button>
                  <button className="btn btn-light" disabled={busy || !message.trim()} onClick={async () => { if (!window.confirm(`Cancel ${request.name}?`)) return; const r = await act({ action: 'cancel', message }, 'Cancelled.'); if (r.ok) setMessage(''); }}>Cancel the request</button>
                </div>
              </div>
            )}
          </section>
        </div>

        <div>
          <section className="tt-card">
            <h2>Traders-only note</h2>
            <div className="tt-field is-wide">
              <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Seen by Investment Management only." />
            </div>
            <div className="tt-actions">
              <button className="btn btn-light btn-sm" disabled={busy || !noteChanged} onClick={() => act({ action: 'note', tradersNote: note }, 'Note saved.')}>Save note</button>
            </div>
          </section>

          <section className="tt-card">
            <h2>Follow-up cases</h2>
            <p className="tt-muted">Each opens the INV case the team opens by hand today, linked to this request and the account.</p>
            {followUpCases.length > 0 && <ul className="tt-list">{followUpCases.map(c => <CaseLine key={c.id} c={c} sfBase={salesforceUrl} />)}</ul>}
            <FollowUps accounts={request.accounts} disabled={busy} onOpen={followUps => act({ action: 'note', followUps }, followUps.length > 1 ? `${followUps.length} cases opened.` : 'Case opened.')} />
          </section>

          <section className="tt-card">
            <h2>Household</h2>
            <dl className="tt-facts">
              <div><dt>PFA number</dt><dd>{household.pfaNumber || '–'}</dd></div>
              <div><dt>Advisor</dt><dd>{household.owner}</dd></div>
              <div><dt>On file</dt><dd>{[household.hasIps && `IPS${household.ipsSignedDate ? ` (signed ${day(household.ipsSignedDate)})` : ''}`, household.ipsAmendment && `IPS amendment${household.ipsAmendmentSignedDate ? ` (signed ${day(household.ipsAmendmentSignedDate)})` : ''}`, household.hasRtq && 'RTQ', household.suitability === 'Advisor Completed' && 'Suitability'].filter(Boolean).join(', ') || 'Nothing'}</dd></div>
              <div><dt>Gains this year</dt><dd>{money(household.ytdLongTermGainLoss) || '$0'} long · {money(household.ytdShortTermGainLoss) || '$0'} short</dd></div>
              {household.mostRecentCashTransaction != null && <div><dt>Most recent cash transaction</dt><dd>{money(household.mostRecentCashTransaction)}</dd></div>}
            </dl>
            {household.tradersOnlyNotes && <p className="tt-note"><strong>Traders-only notes:</strong> {household.tradersOnlyNotes}</p>}
            {household.tradeRequestNotes && <p className="tt-note"><strong>Trade notes:</strong> {household.tradeRequestNotes}</p>}
            {household.mostRecentTransfers && <p className="tt-note"><strong>Recent transfers:</strong> {household.mostRecentTransfers}</p>}
          </section>

          <section className="tt-card">
            <h2>Open cases on the household <span className="tt-count">{openCases.length}</span></h2>
            {openCases.length === 0 ? <p className="tt-muted">None.</p> : <ul className="tt-list">{openCases.map(c => <CaseLine key={c.id} c={c} sfBase={salesforceUrl} />)}</ul>}
          </section>

          <section className="tt-card">
            <h2>Earlier requests for these accounts</h2>
            {previousRequests.length === 0 ? <p className="tt-muted">None.</p> : (
              <ul className="tt-list">
                {previousRequests.map(r => (
                  <li key={r.id}>
                    <a href={`/trade/im/requests/${r.id}`} onClick={e => { e.preventDefault(); navigate(`/trade/im/requests/${r.id}`); }}>{r.name}</a>
                    {' '}{typeLabels(r.types)} <StatusTag status={r.status} /> <span className="tt-muted">· {day(r.createdDate)} · {r.createdBy}</span>
                    <div className="tt-muted tt-small">{(r.accounts || []).join(', ')}{r.instruction ? ` · ${String(r.instruction).slice(0, 140)}${r.instruction.length > 140 ? '…' : ''}` : ''}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
